import HNotification from "../components/custom/notification/HNotification";
import { httpsUrlRegex } from "../pages/utils/commonUtils";
import useHttp from "./useHttp";

function useAttachment(appProperties, fileList, setFileList, alertNotNeeded) {
  const { fetchData } = useHttp();

  let maximumAttachmentLimit = fileList?.length;
  let maximumAttachmentSizeInBytes = fileList?.reduce((total, curr) => {
    if (curr.status === "done") {
      return total + curr.size;
    } else {
      return total;
    }
  }, 0);

  const downloadAFile = async (key, sampleFileName) => {
    try {
      const response = await fetchData(`resource/download?key=${key}`, "GET", null, null, null, null);
      const url = JSON.parse(response)?.data;
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = sampleFileName;
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.error("Error downloading the file:", error);
      HNotification.notify({type: "error", message: "Error", description: "Error downloading the file"});
    }
  };

  const getSupportedAttachmentTypeString = (appProperties) => {
    const supportedAttachmentTypes = appProperties?.providerCommonObj?.supportedAttachmentTypes;
    let finalSupportedAttachmentTypes = "";

    if (supportedAttachmentTypes?.image) {
      finalSupportedAttachmentTypes += supportedAttachmentTypes?.image + ",";
    }

    if (supportedAttachmentTypes?.audio) {
      finalSupportedAttachmentTypes += supportedAttachmentTypes?.audio + ",";
    }

    if (supportedAttachmentTypes?.file) {
      finalSupportedAttachmentTypes += supportedAttachmentTypes?.file + ",";
    }
    if (supportedAttachmentTypes?.video) {
      finalSupportedAttachmentTypes += supportedAttachmentTypes?.video + ",";
    }

    return finalSupportedAttachmentTypes;
  };

  const onError = (uid, setUrl, errorMessage = "Failed to upload the file") => {
    if (setUrl) {
      setUrl("");
    }
    !alertNotNeeded && HNotification.notify({type: "error", message: "MMS Alert", description: errorMessage});
    setFileList(fileList.filter((file) => file.uid !== uid));
  };

  const updatePreview = (uploadedFile) => {
    setFileList([...(fileList?.filter((file) => file.status !== "error" && file.uid !== uploadedFile?.uid) || []), uploadedFile]);
  };

  const checkIsAttachmentLimitReached = () => {
    if (++maximumAttachmentLimit > appProperties.providerCommonObj?.maximumAttachmentLimit) {
      !alertNotNeeded && HNotification.notify({ type: "error", message: "MMS Alert", description: "The MMS Attachment Limit has been exceeded" });
      maximumAttachmentLimit--;
      return true;
    }
    return false;
  };

  const handleBeforeUpload = (file, url, setUrl, setIsInvalidUrl, props) => {
    if (checkIsAttachmentLimitReached()) {
      return false;
    }
    if (file) {
      maximumAttachmentSizeInBytes += file.size;
      if (appProperties?.providerCommonObj?.maximumAttachmentSizeInBytes < maximumAttachmentSizeInBytes) {
        maximumAttachmentSizeInBytes -= file.size;
        !alertNotNeeded && HNotification.notify({ type: "error", message: "MMS Alert", description: "The MMS Attachment Size Limit has been reached" });
        return false;
      }
      return true;
    } else if (url) {
      if (httpsUrlRegex.test(url)) {
        return true;
      } else {
        setUrl("");
        setIsInvalidUrl(true);
        return false;
      }
    }
    return false;
  };
  const uploadAttachment = async (file, url, setUrl, setIsInvalidUrl, setVisible, integId, skipFilter, contactId) => {
    const integIdOrTempValue = integId || "~";

    if (!handleBeforeUpload(file, url, setUrl, setIsInvalidUrl)) {
      return;
    }
    const randomUUID = crypto.randomUUID();
    let uploadedFile = {
      uid: randomUUID,
      status: "uploading",
    };

    updatePreview(uploadedFile);

    try {
      setVisible && setVisible(false);

      // Presigned upload flow for files
      if (file) {
        // Step 1: Get presigned upload URL from API
        const uploadUrlPayload = {
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
        };


        let uploadUrlResponse = await fetchData(
          `${integIdOrTempValue}/attachment/upload-url${skipFilter === "true" ? "?skipFilter=true" : ""}`,
          "POST",
          JSON.stringify(uploadUrlPayload),
          appProperties,
          {
            "Content-Type": "application/json",
          }
        );
        uploadUrlResponse = JSON.parse(uploadUrlResponse);

        if (uploadUrlResponse.status !== 200 || !uploadUrlResponse?.data?.success) {
          const errorMessage = uploadUrlResponse?.data?.message || "Failed to get upload URL";
          onError(uploadedFile.uid, setUrl, errorMessage);
          return false;
        }

        const { uploadUrl, attachmentId, key } = uploadUrlResponse.data;

        // Step 2: Upload file directly to S3 using presigned URL
        const s3Response = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
        });

        if (!s3Response.ok) {
          onError(uploadedFile.uid, setUrl, "Failed to upload file to S3");
          return false;
        }

        // Step 3: Confirm upload with API
        const confirmPayload = {
          attachmentId,
          key,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
        };

        let confirmResponse = await fetchData(
          `${integIdOrTempValue}/attachment/confirm${skipFilter === "true" ? "?skipFilter=true" : ""}`,
          "POST",
          JSON.stringify(confirmPayload),
          appProperties,
          {
            "Content-Type": "application/json",
          }
        );
        confirmResponse = JSON.parse(confirmResponse);

        if (confirmResponse.status === 200 && confirmResponse?.data?.success) {
          const { name, size, url, thumbnailUrl, contentType } = confirmResponse.data?.file;
          uploadedFile = {
            ...uploadedFile,
            name: name,
            status: "done",
            size: size,
            url: url,
            type: contentType,
            contentType: contentType,
            thumbnailUrl: thumbnailUrl,
            contactId: contactId,
          };
          setIsInvalidUrl && setIsInvalidUrl(false);
          updatePreview(uploadedFile);
          return true;
        } else {
          onError(uploadedFile.uid, setUrl, confirmResponse?.data?.message);
          return false;
        }
      }
      // URL upload - keep existing multipart flow
      else if (url) {
        const formData = new FormData();
        formData.append("url", url);

        let response = await fetchData(
          `${integIdOrTempValue}/attachment${skipFilter === "true" ? "?skipFilter=true" : ""}`,
          "POST",
          formData,
          appProperties,
          {
            "Content-Type": "multipart/form-data",
          }
        );
        response = JSON.parse(response);

        if (response.status === 200 && response?.data && response?.data.message === "success") {
          const { name, size, url, thumbnailUrl, contentType } = response.data?.file;
          uploadedFile = {
            ...uploadedFile,
            name: name,
            status: "done",
            size: size,
            url: url,
            type: contentType,
            contentType: contentType,
            thumbnailUrl: thumbnailUrl,
            contactId: contactId,
          };
          setIsInvalidUrl && setIsInvalidUrl(false);
          updatePreview(uploadedFile);
          return true;
        } else {
          onError(uploadedFile.uid, setUrl, response?.data?.message);
          setIsInvalidUrl && setIsInvalidUrl(true);
          return false;
        }
      }
    } catch (e) {
      console.error(e);
      onError(uploadedFile.uid, setUrl);
      return false;
    }
  };
  return { uploadAttachment, getSupportedAttachmentTypeString, downloadAFile };
}

export default useAttachment;
